import {
  DialogTrigger,
  Popover as AriaPopover,
  Button,
  type PopoverProps,
} from "react-aria-components";
import styles from "./Popover.module.scss";
import type { IBaseButton } from "@medoma/components/Button/Button";
import { PopoverTriggerButton } from "@medoma/components/Button/Button";
import type { XOR } from "ts-xor";

type IPopover = {
  trigger: ICustomTrigger;
  children: React.ReactNode;
  controlled?: Pick<PopoverProps, "isOpen" | "onOpenChange">;
};

type ICustomTrigger = Pick<IBaseButton, "weight" | "size" | "disabled"> &
  XOR<
    { icon: React.ReactNode; text: string },
    XOR<
      { icon: React.ReactNode; ariaLabel: string },
      { custom: React.ReactNode; ariaLabel?: string }
    >
  >;

const CustomTrigger = ({
  text,
  icon,
  custom,
  ariaLabel,
  weight = "light",
  size,
  disabled = false,
}: ICustomTrigger) => {
  if (custom !== undefined) {
    return (
      <Button className={styles.customTrigger} aria-label={ariaLabel}>
        {custom}
      </Button>
    );
  }

  if (text !== undefined) {
    return (
      <PopoverTriggerButton
        contentType="text-and-icon"
        weight={weight}
        size={size}
        disabled={disabled}
      >
        {icon}
        {text}
      </PopoverTriggerButton>
    );
  }

  /* fallback <=> "icon" */
  return (
    <div className={styles.fallbackTrigger}>
      <PopoverTriggerButton
        contentType="text-and-icon"
        weight={weight}
        size={size}
        aria-label={ariaLabel}
      >
        {icon}
      </PopoverTriggerButton>
    </div>
  );
};

export const Popover = ({ trigger, children, controlled }: IPopover) => {
  return (
    <DialogTrigger
      isOpen={controlled?.isOpen}
      onOpenChange={controlled?.onOpenChange}
    >
      <CustomTrigger {...trigger} />
      <AriaPopover>
        <div className={styles.content}>{children}</div>
      </AriaPopover>
    </DialogTrigger>
  );
};
